/**
 * Resolver state machine behind NmiCardForm.tokenize().
 *
 * Why this exists as its own module: Collect.js's startPaymentRequest()
 * ABORTS on field validation failure and then calls NEITHER the token
 * `callback` NOR `timeoutCallback`. The pending promise therefore never
 * settled — the submit button sat on "Processing…", no confirm-payment call
 * was made, and the patient saw no error at all. Production orders #896 and
 * #897 are exactly this: a checkout.create with no order.confirm ever
 * following, and a second click producing an idempotent replay.
 *
 * Three guarantees:
 *   1. a validation failure REJECTS the pending tokenize
 *   2. an independent watchdog rejects if Collect.js goes silent, so we never
 *      depend on its own timeoutCallback firing
 *   3. the resolver is always cleared, so a stuck attempt cannot poison the
 *      next one with "already in progress"
 */
'use strict';

const DEFAULT_WATCHDOG_MS = 20000;

function createTokenizeController({ watchdogMs = DEFAULT_WATCHDOG_MS, setTimeoutFn, clearTimeoutFn } = {}) {
  const setT = setTimeoutFn || ((fn, ms) => setTimeout(fn, ms));
  const clearT = clearTimeoutFn || ((h) => clearTimeout(h));

  let pending = null;   // { resolve, reject, timer }

  function settle(kind, payload) {
    if (!pending) return false;
    const p = pending;
    pending = null;
    if (p.timer) clearT(p.timer);
    if (kind === 'resolve') p.resolve(payload);
    else p.reject(payload);
    return true;
  }

  return {
    isPending: () => pending !== null,

    /** @returns {Promise<string>} the single-use payment token */
    start(startRequest) {
      if (pending) {
        return Promise.reject(new Error('A card verification is already in progress'));
      }
      return new Promise((resolve, reject) => {
        pending = { resolve, reject, timer: null };
        pending.timer = setT(() => {
          settle('reject', new Error(
            'The card form did not respond. Nothing was charged — please re-enter the card and try again.'));
        }, watchdogMs);
        try {
          startRequest();
        } catch (e) {
          settle('reject', new Error('Could not start card verification'));
        }
      });
    },

    /** Collect.js token callback. */
    onCallback(response) {
      if (response && response.token) return settle('resolve', response.token);
      return settle('reject', new Error('Could not verify card. Please check the details and try again.'));
    },

    /**
     * Collect.js validation callback. Only a FAILURE while a tokenize is
     * pending ends the attempt — a valid field, or a failure while idle (the
     * user simply typing), must not reject anything.
     */
    onValidation(field, valid, message) {
      if (valid || !pending) return false;
      return settle('reject', new Error(message || `Please check the ${field} field and try again.`));
    },

    /** Collect.js timeoutCallback. */
    onTimeout() {
      return settle('reject', new Error('Card verification timed out. Please try again.'));
    },

    /** Unmount / modal close: never leave a resolver behind. */
    cancel(reason = 'Card entry was closed before the payment completed.') {
      return settle('reject', new Error(reason));
    },
  };
}

module.exports = { createTokenizeController, DEFAULT_WATCHDOG_MS };
